import * as React from 'react';
import PhoneInputWithCountrySelect from 'react-phone-number-input';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { parsePhoneNumber } from 'libphonenumber-js';
import { Typography } from '@rtkwlf/ui';
import flags from 'react-phone-number-input/flags';

type Props = {
  name: string;
  required: boolean;
  disabled?: boolean;
};

const PhoneNumberError = styled.span`
  color: #c43836;
  user-select: none;
  font-family: 'Lato', sans-serif;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 1.5;
`;

PhoneNumberError.displayName = 'PhoneNumberError';

const PhoneNumberInputContainer = styled(PhoneInputWithCountrySelect)`
  .PhoneInputInput {
    flex: 1;
    min-width: 0;
    height: 37px;
    border: 1px solid #cccccc;
    border-radius: 4px;
  }
  svg {
    margin-top: -0.65rem;
  }
`;

PhoneNumberInputContainer.displayName = 'PhoneNumberInputContainer';

export const INVALID_PHONE_NUMBER = 'Enter a valid phone number';

/**
 * Builds a phone number input with PhoneInputWithCountrySelect
 * with a custom error message since PhoneInputWithCountrySelect doesn't support error messages
 *
 * Must be used within a formik context
 */
const PhoneNumberInput: React.FunctionComponent<Props> = ({
  name,
  required,
  disabled,
}) => {
  const { setFieldValue, errors, values } = useFormikContext<any>();

  const value = values[name];

  const onChange = (value: string) => {
    setFieldValue(name, value);
  };

  React.useEffect(() => {
    // Convert legacy phone number to international
    if (value && !value.includes('+')) {
      const phoneNumber = parsePhoneNumber(value, 'US');
      if (phoneNumber) {
        setFieldValue(
          name,
          phoneNumber.formatInternational().replace(/ /g, ''),
        );
      }
    }
  }, [name, setFieldValue, value]);

  return (
    <div>
      <Typography.Label className={required ? 'required' : ''}>
        Phone Number
      </Typography.Label>
      <PhoneNumberInputContainer
        name={name}
        flags={flags}
        defaultCountry="US"
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
      {errors[name] && <PhoneNumberError>{errors[name]}</PhoneNumberError>}
    </div>
  );
};

export default PhoneNumberInput;
